import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';

import { useUpdateStore } from '@/stores/updateStore';

/**
 * Dismissible top banner shown when hoopla-vendor's `X-App-Update-*` response
 * headers report a newer non-mandatory release for this packaged build (see
 * ForceUpdateScreen for the mandatory case). Sits above OfflineBar (z-[65])
 * so an update notice is never hidden behind a connectivity notice.
 */
export function UpdateBanner() {
  const { t } = useTranslation();
  const isUpdateAvailable = useUpdateStore((s) => s.isUpdateAvailable);
  const isForceUpdate = useUpdateStore((s) => s.isForceUpdate);
  const latestVersion = useUpdateStore((s) => s.latestVersion);
  const downloadUrl = useUpdateStore((s) => s.downloadUrl);
  const [dismissed, setDismissed] = useState(false);

  if (typeof document === 'undefined') return null;
  if (!isUpdateAvailable || isForceUpdate || dismissed) return null;

  return createPortal(
    <div
      role="alert"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-3 bg-primary px-4 py-2 text-sm text-primary-foreground shadow-[0_4px_12px_-4px_rgba(0,0,0,0.25)] animate-slide-in-down"
    >
      <Download className="h-4 w-4 shrink-0" />
      <span className="font-medium">
        {t('update.bannerTitle', { version: latestVersion ?? '' })}
      </span>
      {downloadUrl && (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noreferrer"
          className="ml-1 inline-flex items-center gap-1 rounded-full border border-primary-foreground/30 bg-primary-foreground/10 px-3 py-0.5 text-xs font-semibold uppercase tracking-[0.18em] transition-colors hover:bg-primary-foreground/20"
        >
          {t('update.updateNow')}
        </a>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('common.close')}
        className="ml-1 shrink-0 rounded-full p-1 transition-colors hover:bg-primary-foreground/20"
      >
        <X className="h-4 w-4" />
      </button>
    </div>,
    document.body,
  );
}
